import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { ROLES } from '../../services/Constants';
import { Link, useLocation } from 'react-router-dom';

const SidebarContainer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 300px;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.card};
  box-shadow: -4px 0 20px ${({ theme }) => theme.colors.shadow};
  padding: 1.5rem 1.25rem;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  transform: ${({ isOpen }) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s ease-in-out;
  box-sizing: border-box;
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,0.4);
  z-index: 900;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  transition: opacity 0.3s ease;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: 0;
  opacity: 0.6;
  &:hover { opacity: 1; }
`;

const SidebarLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme, isActive }) => (isActive ? theme.colors.primary : theme.colors.text)};
  padding: 0.45rem 0.75rem;
  display: block;
  border-left: 3px solid ${({ theme, isActive }) => (isActive ? theme.colors.primary : 'transparent')};
  border-radius: 0 6px 6px 0;
  font-size: 0.9rem;
  background: ${({ theme, isActive }) => (isActive ? theme.colors.primary + '12' : 'transparent')};
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary + '0d'};
  }
`;

const SectionLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.lineColor};
  margin: 0 0 0.35rem 0.75rem;
`;

const SectionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding-bottom: 1rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lineColor}33;
`;

const AccordionSection = styled.div`
  margin-bottom: 0.25rem;
`;

const AccordionHeader = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.9rem;
  font-weight: 600;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  text-align: left;
  border-radius: 6px;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.primary + '0d'};
  }
`;

const AccordionLabel = styled.span`
  color: ${({ theme }) => theme.colors.primary};
`;

const Chevron = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.lineColor};
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(0deg)' : 'rotate(-90deg)')};
  display: inline-block;
`;

const AccordionContent = styled.div`
  overflow: hidden;
  max-height: ${({ $open }) => ($open ? '500px' : '0')};
  transition: max-height 0.25s ease;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding-left: 0.5rem;
  padding-bottom: ${({ $open }) => ($open ? '0.5rem' : '0')};
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 10px;
  margin-bottom: 1.25rem;
`;

const UserAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const UserName = styled.span`
  font-weight: 600;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.colors.lineColor};
`;

const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Administrator',
  [ROLES.EMPLOYEE]: 'Zaposlenik',
  [ROLES.USER]: 'Korisnik',
};

function useSectionState(location) {
    const path = location.pathname;
    return useState(() => ({
        general: ['/dashboard', '/postavke'].includes(path),
        users: ['/registracija', '/korisnici'].includes(path),
        books: ['/posudbePregled', '/novaKnjiga', '/posudbaKnjige', '/vracanjeKnjige', '/zanrovi', '/katalog'].includes(path),
        employees: ['/noviZaposlenik', '/sviZaposlenici', '/raspored'].includes(path),
    }));
}

const Sidebar = ({ isOpen, toggleSidebar, user, hasMessages }) => {
    const location = useLocation();
    const theme = useTheme();
    const [open, setOpen] = useSectionState(location);

    if (!user) return null;

    const toggle = (key) => setOpen(prev => ({ ...prev, [key]: !prev[key] }));
    const isActive = (path) => location.pathname === path;

    const initials = [user.name, user.surname]
        .filter(Boolean)
        .map(s => s[0].toUpperCase())
        .join('') || user.username?.[0]?.toUpperCase() || '?';

    const fullName = [user.name, user.surname].filter(Boolean).join(' ') || user.username;

    return (
        <>
            <Overlay isOpen={isOpen} onClick={toggleSidebar} />
            <SidebarContainer isOpen={isOpen}>

                {/* Header */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "1.25rem",
                    paddingBottom: "1rem",
                    borderBottom: `2px solid ${theme.colors.primary}`,
                }}>
                    <h3 style={{ margin: 0, fontSize: "1.75rem", color: theme.colors.primary, fontWeight: 800 }}>
                        Libra
                    </h3>
                    <CloseButton onClick={toggleSidebar}>×</CloseButton>
                </div>

                {/* User info */}
                <UserCard>
                    <UserAvatar>{initials}</UserAvatar>
                    <UserInfo>
                        <UserName>{fullName}</UserName>
                        <UserRole>{ROLE_LABELS[user.role] ?? user.role}</UserRole>
                    </UserInfo>
                </UserCard>

                {/* Personal links */}
                <SectionLabel>Moj prostor</SectionLabel>
                <SectionGroup>
                    <div style={{ position: "relative" }}>
                        <SidebarLink to="/obavijesti" isActive={isActive('/obavijesti')} onClick={toggleSidebar}>
                            Obavijesti
                        </SidebarLink>
                        {hasMessages && (
                            <img
                                src={require('../../assets/notification.png')}
                                alt="Nove poruke"
                                style={{ position: "absolute", top: "6px", right: "8px", width: "8px", height: "8px" }}
                            />
                        )}
                    </div>
                    <SidebarLink to="/povijest" isActive={isActive('/povijest')} onClick={toggleSidebar}>
                        Povijest posudbi
                    </SidebarLink>
                    <SidebarLink to="/preporuke" isActive={isActive('/preporuke')} onClick={toggleSidebar}>
                        Preporučene knjige
                    </SidebarLink>
                </SectionGroup>

                {/* Employee + Admin accordion sections */}
                {(user.role === ROLES.EMPLOYEE || user.role === ROLES.ADMIN) && (
                    <>
                        <SectionLabel style={{ marginTop: "0.5rem" }}>Upravljanje</SectionLabel>

                        <AccordionSection>
                            <AccordionHeader onClick={() => toggle('general')}>
                                <AccordionLabel>Općenito</AccordionLabel>
                                <Chevron $open={open.general}>▾</Chevron>
                            </AccordionHeader>
                            <AccordionContent $open={open.general}>
                                <SidebarLink to="/dashboard" isActive={isActive('/dashboard')} onClick={toggleSidebar}>
                                    Dashboard
                                </SidebarLink>
                                {user.role === ROLES.ADMIN && (
                                    <SidebarLink to="/postavke" isActive={isActive('/postavke')} onClick={toggleSidebar}>
                                        Postavke aplikacije
                                    </SidebarLink>
                                )}
                            </AccordionContent>
                        </AccordionSection>

                        <AccordionSection>
                            <AccordionHeader onClick={() => toggle('users')}>
                                <AccordionLabel>Korisnici</AccordionLabel>
                                <Chevron $open={open.users}>▾</Chevron>
                            </AccordionHeader>
                            <AccordionContent $open={open.users}>
                                <SidebarLink to="/registracija" isActive={isActive('/registracija')} onClick={toggleSidebar}>
                                    Registracija korisnika
                                </SidebarLink>
                                <SidebarLink to="/korisnici" isActive={isActive('/korisnici')} onClick={toggleSidebar}>
                                    Pregled korisnika
                                </SidebarLink>
                            </AccordionContent>
                        </AccordionSection>

                        <AccordionSection>
                            <AccordionHeader onClick={() => toggle('books')}>
                                <AccordionLabel>Knjige</AccordionLabel>
                                <Chevron $open={open.books}>▾</Chevron>
                            </AccordionHeader>
                            <AccordionContent $open={open.books}>
                                <SidebarLink to="/posudbePregled" isActive={isActive('/posudbePregled')} onClick={toggleSidebar}>
                                    Pregled posudbi
                                </SidebarLink>
                                <SidebarLink to="/novaKnjiga" isActive={isActive('/novaKnjiga')} onClick={toggleSidebar}>
                                    Dodaj novu knjigu
                                </SidebarLink>
                                <SidebarLink to="/posudbaKnjige" isActive={isActive('/posudbaKnjige')} onClick={toggleSidebar}>
                                    Posudba knjige
                                </SidebarLink>
                                <SidebarLink to="/vracanjeKnjige" isActive={isActive('/vracanjeKnjige')} onClick={toggleSidebar}>
                                    Vraćanje knjige
                                </SidebarLink>
                                <SidebarLink to="/katalog" isActive={isActive('/katalog')} onClick={toggleSidebar}>
                                    Katalog
                                </SidebarLink>
                                <SidebarLink to="/zanrovi" isActive={isActive('/zanrovi')} onClick={toggleSidebar}>
                                    Žanrovi
                                </SidebarLink>
                            </AccordionContent>
                        </AccordionSection>

                        <AccordionSection>
                            <AccordionHeader onClick={() => toggle('employees')}>
                                <AccordionLabel>Zaposlenici</AccordionLabel>
                                <Chevron $open={open.employees}>▾</Chevron>
                            </AccordionHeader>
                            <AccordionContent $open={open.employees}>
                                {user.role === ROLES.ADMIN && (
                                    <SidebarLink to="/noviZaposlenik" isActive={isActive('/noviZaposlenik')} onClick={toggleSidebar}>
                                        Registracija zaposlenika
                                    </SidebarLink>
                                )}
                                {user.role === ROLES.ADMIN && (
                                    <SidebarLink to="/sviZaposlenici" isActive={isActive('/sviZaposlenici')} onClick={toggleSidebar}>
                                        Pregled zaposlenika
                                    </SidebarLink>
                                )}
                                <SidebarLink to="/raspored" isActive={isActive('/raspored')} onClick={toggleSidebar}>
                                    Raspored
                                </SidebarLink>
                            </AccordionContent>
                        </AccordionSection>
                    </>
                )}
            </SidebarContainer>
        </>
    );
};

export default Sidebar;
